import { useEffect, useState } from 'react'
import {
  type ApiDocumentRequestDetails,
  type ApiFinancialEvent,
  fetchDocumentRequest,
  regenerateDocumentRequest,
} from '@/fetcher'
import {
  DOCUMENT_REQUEST_STATUS_LABELS,
  FINANCIAL_EVENT_TYPE_LABELS,
  formatDateTime,
  formatRub,
  formatUsdt,
} from '@/helpers/format'
import { useNav } from '@/nav'

type RequestInfoPageProps = {
  requestId: string
}

type InfoRow = {
  label: string
  value: string | null
}

const InfoRowItem = ({ label, value }: InfoRow) => {
  if (value === null) return null
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-xs text-[var(--app-muted)]">{label}</dt>
      <dd className="min-w-0 break-words text-right text-sm text-[var(--app-text)]">
        {value}
      </dd>
    </div>
  )
}

const Card = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <section className="rounded-[18px] bg-[var(--app-input)] px-4 py-3">
    <h2 className="mb-3 text-sm font-semibold text-[var(--app-text)]">
      {title}
    </h2>
    <dl className="space-y-2">{children}</dl>
  </section>
)

const INDIVIDUAL_DATA_FIELDS: { label: string; key: string }[] = [
  { label: 'ФИО (RU)', key: 'fullNameRu' },
  { label: 'ФИО (EN)', key: 'fullNameEn' },
  { label: 'Дата рождения', key: 'birthDate' },
  { label: 'Серия и номер паспорта', key: 'passportSeriesNumber' },
  { label: 'Дата выдачи паспорта', key: 'passportIssueDate' },
  { label: 'Код подразделения', key: 'passportDivisionCode' },
  { label: 'Поставщик', key: 'supplierName' },
  { label: 'Номер инвойса', key: 'invoiceNo' },
  { label: 'Дата инвойса', key: 'invoiceDate' },
  { label: 'Сумма инвойса', key: 'invoiceAmount' },
  { label: 'Валюта инвойса', key: 'invoiceCurrency' },
  { label: 'Наименование товара', key: 'productName' },
  { label: 'Курс конвертации', key: 'conversionRate' },
  { label: 'Сумма в рублях', key: 'amountRub' },
  { label: 'Дата контракта', key: 'contractDate' },
]

const LEGAL_DATA_FIELDS: { label: string; key: string }[] = [
  { label: 'Тип юрлица', key: 'legalType' },
  { label: 'ФИО (RU)', key: 'fullNameRu' },
  { label: 'ФИО (EN)', key: 'fullNameEn' },
  { label: 'Название организации (RU)', key: 'organizationNameRu' },
  { label: 'Organization name (EN)', key: 'organizationNameEn' },
  { label: 'Должность (RU)', key: 'positionRu' },
  { label: 'Position (EN)', key: 'positionEn' },
  { label: 'Адрес (RU)', key: 'legalAddressRu' },
  { label: 'Address (EN)', key: 'legalAddressEn' },
  { label: 'Фактический адрес (RU)', key: 'legalActualAddressRu' },
  { label: 'Actual address (EN)', key: 'legalActualAddressEn' },
  { label: 'ОГРНИП (PSRNSP)', key: 'psrnsp' },
  { label: 'КПП', key: 'kpp' },
  { label: 'ОГРН', key: 'ogrn' },
  { label: 'ИНН (TIN)', key: 'tin' },
  { label: 'Банк (RU)', key: 'legalBankRu' },
  { label: 'Bank (EN)', key: 'legalBankEn' },
  { label: 'Расчетный счет', key: 'legalSettlementAccount' },
  { label: 'Корреспондентский счет', key: 'legalCorrespondentAccount' },
  { label: 'БИК (BIC)', key: 'bic' },
  { label: 'Поставщик', key: 'supplierName' },
  { label: 'Номер инвойса', key: 'invoiceNo' },
  { label: 'Дата инвойса', key: 'invoiceDate' },
  { label: 'Сумма инвойса', key: 'invoiceAmount' },
  { label: 'Валюта инвойса', key: 'invoiceCurrency' },
  { label: 'Наименование товара', key: 'productName' },
  { label: 'Курс конвертации', key: 'conversionRate' },
  { label: 'Сумма в рублях', key: 'amountRub' },
  { label: 'Дата контракта', key: 'contractDate' },
]

const EventCard = ({ event }: { event: ApiFinancialEvent }) => {
  const rows: InfoRow[] = [
    {
      label: 'RUB',
      value: event.rubAmount ? `${formatRub(event.rubAmount)} ₽` : null,
    },
    {
      label: 'USDT',
      value: event.usdtAmount ? formatUsdt(event.usdtAmount) : null,
    },
    {
      label: 'Курс USDT',
      value: event.usdtRate ? formatUsdt(event.usdtRate) : null,
    },
  ]

  return (
    <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="rounded-full bg-[var(--app-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--app-accent)]">
          {FINANCIAL_EVENT_TYPE_LABELS[event.type]}
        </span>
        <span className="text-xs text-[var(--app-muted)]">
          {formatDateTime(event.createdAt)}
        </span>
      </div>
      <dl className="space-y-2">
        {rows.map((row) => (
          <InfoRowItem key={row.label} {...row} />
        ))}
      </dl>
    </div>
  )
}

export const RequestInfoPage = ({ requestId }: RequestInfoPageProps) => {
  const { navigate } = useNav()
  const [details, setDetails] = useState<ApiDocumentRequestDetails | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (!requestId) return
    let isMounted = true
    setIsLoading(true)
    setErrorMessage(null)
    fetchDocumentRequest(requestId)
      .then((payload) => {
        if (isMounted) setDetails(payload)
      })
      .catch((error) => {
        if (isMounted) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : 'Не удалось загрузить заявку',
          )
          setDetails(null)
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [requestId])

  if (isLoading) {
    return (
      <div className="py-8 mx-auto max-w-2xl px-4 text-center text-sm text-[var(--app-muted)]">
        Загрузка…
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="py-8 mx-auto max-w-2xl px-4">
        <div className="rounded-[18px] bg-[var(--app-danger-soft)] px-4 py-3 text-sm text-[var(--app-text)]">
          {errorMessage}
        </div>
      </div>
    )
  }

  if (!details) {
    return (
      <div className="py-8 mx-auto max-w-2xl px-4 text-center text-sm text-[var(--app-muted)]">
        Заявка не найдена
      </div>
    )
  }

  const { request: r, events } = details
  const canEdit = r.status === 'created'

  const handleRegenerate = async () => {
    setIsRegenerating(true)
    setActionError(null)
    setActionMessage(null)
    try {
      await regenerateDocumentRequest(requestId)
      setActionMessage('Документы придут вам в сообщении от Telegram-бота')
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'Не удалось запустить генерацию документов',
      )
    } finally {
      setIsRegenerating(false)
    }
  }

  const detailRows: InfoRow[] = [
    { label: 'Сумма RUB', value: `${formatRub(r.amountRub)} ₽` },
    { label: 'Сумма USDT', value: formatUsdt(r.clientPaidUsdtAmount) },
    { label: 'Курс USDT', value: formatUsdt(r.clientPaidUsdtRate) },
    { label: 'Создано', value: formatDateTime(r.createdAt) },
    {
      label: 'Оплачено клиентом',
      value: r.clientPaidAt ? formatDateTime(r.clientPaidAt) : null,
    },
    {
      label: 'Зачислено банком',
      value: r.bankReceivedAt ? formatDateTime(r.bankReceivedAt) : null,
    },
    {
      label: 'Отменено',
      value: r.cancelledAt ? formatDateTime(r.cancelledAt) : null,
    },
    { label: 'Обновлено', value: formatDateTime(r.updatedAt) },
    {
      label: 'Номер поручения',
      value: r.agencyOrderNo != null ? String(r.agencyOrderNo) : null,
    },
  ]

  return (
    <div className="py-8 mx-auto max-w-2xl px-4 space-y-4 pb-40">
      <section className="rounded-[18px] bg-[var(--app-input)] px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h1 className="truncate text-lg font-semibold text-[var(--app-text)]">
              Заявка {r.contractNo}
            </h1>
            <p className="text-xs text-[var(--app-muted)]">
              {r.invoiceNo ?? '—'} · {r.teamName}
            </p>
          </div>
          <div className="shrink-0 space-y-2 text-right">
            <span className="inline-block rounded-full bg-[var(--app-accent-soft)] px-3 py-1 text-xs font-medium text-[var(--app-accent)]">
              {DOCUMENT_REQUEST_STATUS_LABELS[r.status]}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void handleRegenerate()}
          disabled={isRegenerating}
          className="mt-4 w-full rounded-[14px] border border-[var(--app-accent-soft)] bg-[var(--app-input)] px-4 py-3 text-sm font-medium transition text-[var(--app-text)] disabled:opacity-50"
        >
          {isRegenerating ? 'Запуск генерации...' : 'Сгенерировать документы'}
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={() => navigate('docs', { params: { requestId: r.id } })}
            className="mt-2 w-full rounded-[14px] bg-[var(--app-accent)] px-4 py-3 text-sm font-semibold text-[var(--app-accent-text)] transition hover:opacity-90"
          >
            Редактировать
          </button>
        )}
        {actionMessage && (
          <p className="mt-3 text-center text-xs text-[var(--app-muted)]">
            {actionMessage}
          </p>
        )}
        {actionError && (
          <p className="mt-3 text-center text-xs text-[var(--app-danger)]">
            {actionError}
          </p>
        )}
      </section>

      <Card title="Детали">
        {detailRows.map((row) => (
          <InfoRowItem key={row.label} {...row} />
        ))}
      </Card>

      <Card title="Данные заявки">
        {(r.data.clientType === 'legal'
          ? LEGAL_DATA_FIELDS
          : INDIVIDUAL_DATA_FIELDS
        ).map(({ label, key }) => (
          <InfoRowItem key={key} label={label} value={r.data[key] ?? null} />
        ))}
      </Card>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-[var(--app-text)]">
          Финансовые события
        </h2>
        {events.length === 0 ? (
          <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-6 text-center text-sm text-[var(--app-muted)]">
            Событий нет
          </div>
        ) : (
          <div className="space-y-2">
            {events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
